import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, OnInit, ViewChild } from '@angular/core';
import {
  AbcNumberBoxComponent,
  AbcPopupComponent,
  _AbcButtonComponent,
  _AbcProgressBarComponent,
  _AbcTextAreaComponent,
  _AbcTooltipComponent,
} from '@barthauer/abc/ui-components';
import { DxButtonModule, DxScrollViewModule } from 'devextreme-angular';
import { DataService } from '../../../../services/data.service';
import { SharedModule } from '../../../../shared/shared.module';
import { MeasureService } from '../../measure.service';
import { RatingComponent } from '../../../../shared/rating/rating.component';
import { CommonModule } from '@angular/common';
import { RatingViewModel } from '../../../../view-models/rating.view-model';
import { AbcCore } from '@barthauer/abc/core';
import { PermissionContext, ActionType, ToastType } from '../../../../models/enumeration-list.model';
import { NotificationService } from '../../../../services/notification.service';
import { NotificationMessage } from '@barthauer/custom/xmt-models';
import { AppService } from '../../../../services/app.service';
import { finalize, first, switchMap } from 'rxjs';
import { EURO_SVG, ICON_SVG } from '../../../../shared/assets/icons';

export interface IRating {
  Id?: string;
  measureId: string;
  comment?: string;
  Impact: number;
  Cost: number;
  TotalScore: number;
  Effort: number;
  Benefit: number;
  createdAt?: Date;
  personFullName?: string;
  IdPerson?: string;
  commentId?: string;
}

@Component({
  selector: 'ubt-geods-measure-evaluation',
  templateUrl: './measure-evaluation.component.html',
  styleUrls: ['./measure-evaluation.component.scss'],
  imports: [
    DxButtonModule,
    SharedModule,
    _AbcButtonComponent,
    RatingComponent,
    AbcNumberBoxComponent,
    _AbcProgressBarComponent,
    _AbcTextAreaComponent,
    AbcPopupComponent,
    CommonModule,
    DxScrollViewModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasureEvaluationComponent implements OnInit {
  @ViewChild('ratingPopup') ratingPopup: AbcPopupComponent;
  @ViewChild('ratingHistoryPopup') ratingHistoryPopup: AbcPopupComponent;

  protected translator = this.dataService.res;
  protected iconSvg = ICON_SVG
  protected euroSvg = EURO_SVG;

  protected fillColor: string = '#c91f8e';
  protected emptyColor: string = '#D7C1CF';
  protected ratingCount: number = 6;
  protected measureEffect: number = 0;
  protected totalScore: number = 0;
  protected maxScore: number = 36;
  protected measureEffort: number = 0;
  protected measureBenefit: number = 0;
  protected measureImpact: number = 0;
  protected measureCosts: number = 0;
  protected comment: string = '';
  protected rating: RatingViewModel = new RatingViewModel({
    comment: this.comment,
    Impact: this.measureImpact,
    Cost: this.measureCosts,
    Effort: this.measureEffort,
    Benefit: this.measureBenefit,
    TotalScore: this.totalScore,
  });
  protected currentRating: RatingViewModel = new RatingViewModel(null);
  protected ratingHistory: RatingViewModel[] = [];
  protected canEdit: boolean = false;
  private currentUserFullName: string = '';
  private readonly delimiter: string = '#####';
  private personId: string = '';
  constructor(
    private dataService: DataService,
    private MeasureService: MeasureService,
    private abcCore: AbcCore,
    private notificationService: NotificationService,
    private appService: AppService,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const measure = this.MeasureService.currentMeasure();
      if (measure && measure.Id) {
        const ratingInfo = measure?.comment?.Text?.split(this.delimiter);
        this.rating = new RatingViewModel({
          Effort: measure.Effort || 0,
          Benefit: measure.Benefit || 0,
          TotalScore: measure.TotalScore || 0,
          Impact: measure.Impact || 0,
          Cost: measure.Cost || 0,
          comment: ratingInfo?.[0] ? ratingInfo[0].trim() : '',
          measureId: measure.Id,
          createdAt: ratingInfo?.[2] ? new Date(ratingInfo[2] || '') : null,
          personFullName: ratingInfo?.[1] ? ratingInfo[1].trim() : '',
          commentId: measure?.comment?.Id,
        });
        this.currentRating = new RatingViewModel(this.rating.toDto());
        if (this.rating.createdAt) {
          this.ratingHistory.push(this.rating);
        }
        this.cdr.markForCheck();
      }
    });
  }
  ngOnInit() {
    this.appService.loggedInUserInformations$.pipe(first()).subscribe((info) => {
      this.currentUserFullName = info.fullName;
      this.personId = info.id;
      this.cdr.detectChanges();
    });
  }

  /**
   * Formats the status string for display.
   * Returns the total score and maximum score separated by a slash.
   */
  protected formatStaus = (): string => {
    return `${this.rating.TotalScore}/${this.maxScore}`;
  };

  /**
   * Updates the measure effort rate.
   * Calculates the total score based on the new effort and benefit rates.
   * Triggers the value change event to validate the rating.
   */
  protected onUpdateEffortRate = (value: number) => {
    this.rating.Effort = value;
    this.calculateToalRate();
  };

  /**
   * Updates the measure benefit rate.
   * Calculates the total score based on the new effort and benefit rates.
   * Triggers the value change event to validate the rating.
   */
  protected onUpdateBenefitRate = (value: number) => {
    this.rating.Benefit = value;
    this.calculateToalRate();
  };

  /**
   * Calculates the total score based on the effort and benefit rates.
   * Updates the total score property of the rating object.
   * Triggers the value change event to validate the rating.
   */
  private calculateToalRate = () => {
    this.rating.TotalScore = this.rating.Effort * this.rating.Benefit;
    this.onValueChange();
  };

  protected showRatingPopup() {
    this.currentRating = new RatingViewModel(this.rating.toDto());
    this.ratingPopup.instance.show();
    this.cdr.markForCheck();
  }

  protected showRatingHistory = () => {
    this.ratingHistoryPopup.instance.show();
  };

  protected hideRatingHistory = () => {
    this.ratingHistoryPopup.instance.hide();
  };

  /**
   * Handles the value change event for the rating form.
   * Validates the rating and checks if it has changed from the current rating.
   * Enables the submit button if the rating is valid and has changed.
   */
  protected onValueChange = () => {
    const requiredFields = ['Effort', 'Benefit', 'TotalScore', 'Impact', 'Cost', 'comment'];
    const isValid = this.validateObject(this.rating.toDto(), requiredFields);
    const hasChanged = this.dataService.compareObjects(this.currentRating.toDto(), this.rating.toDto());
    this.canEdit = isValid && hasChanged;
    this.cdr.markForCheck();
  };

  /**
   * Submits the updated rating.
   * Validates the rating, updates the measure comment, and publishes the updated rating.
   */
  protected submit() {
    if (!this.canEdit) {
      return;
    }
    this.canEdit = false;
    const ratingDto = this.rating.toDto();
    const updateDate = new Date();
    this.appService.showLoader(true);
    ratingDto.comment = `${ratingDto.comment} ${this.delimiter}${this.currentUserFullName}${this.delimiter}${updateDate}`;
    ratingDto.IdPerson = this.personId;
    this.MeasureService.updateMeasureRating(ratingDto)
      .pipe(
        switchMap((res) => {
          return this.MeasureService.updateMeasureRatingComment(ratingDto, ratingDto.commentId);
        }),
        finalize(() => {
          this.appService.showLoader(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.rating.createdAt = updateDate;
          this.rating.personFullName = this.currentUserFullName;
          this.ratingHistory[0] = this.rating;
          this.ratingPopup.instance.hide();
        },
        error: (error) => {
          this.canEdit = true;
          this.notificationService.callNotification({
            message: NotificationMessage.ErrorGeneral,
            type: ToastType.ERROR,
          });
          this.abcCore.error.publish(error);
        },
      });
  }

  /**
   * Toggles the comment text between a clamped and unclamped state.
   * @param card - The HTML element containing the comment text.
   */
  protected toggleComment = (card: HTMLElement) => {
    const text = card.querySelector('.comment-text') as HTMLElement;

    if (text.classList.contains('line-clamp')) {
      text.classList.remove('line-clamp');
      card.querySelector('button')!.textContent = this.translator('Ubt-Measure-Detail-ShowLess');
    } else {
      text.classList.add('line-clamp');
      card.querySelector('button')!.textContent = this.translator('Ubt-Measure-Detail-ShowMore');
    }
  };

  /**
   * Cancels the edit operation and restores the previous rating.
   */
  protected cancelEdit = () => {
    this.rating = new RatingViewModel(this.currentRating.toDto());
    this.canEdit = false;
    this.ratingPopup.instance.hide();
  };

  /**
   * Validates an object against a list of required fields.
   * @param obj - The object to validate.
   * @param requiredFields - The list of required field names.
   * @returns True if the object has all required fields with non-empty values, false otherwise.
   */
  private validateObject(obj: any, requiredFields: string[]): boolean {
    return requiredFields.every((field) => obj[field] !== null && obj[field] !== undefined && obj[field] !== '');
  }

  /**
   * Trims the full name of a person to its initials.
   * @param rating - The rating object containing the person's full name.
   * @returns The initials of the person's full name.
   */
  public trimmedUserName = (rating: RatingViewModel): string => {
    const trimmedName = rating.personFullName?.trim() || ' ';
    return trimmedName.length > 1 ? trimmedName[0][0] + trimmedName[1][0] : trimmedName[0][1];
  };

  /**
   * Checks if the current user has permission to update the measure rating.
   * @returns True if the user has permission, false otherwise.
   */
  public checkPermission = (): boolean => {
    return this.dataService.hasPermission(PermissionContext.MeasureRatingUpdate, ActionType.SEE);
  };
}
